import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Error, Refresh, Payment, Home } from '@mui/icons-material';

/**
 * Payment Failed Page
 * Shown when payment fails or is cancelled
 */
const PaymentFailed = () => {
  const navigate = useNavigate();

  const troubleshootingSteps = [
    'Check if your card has sufficient funds',
    'Verify your card details are correct',
    'Try a different payment method',
    'Contact your bank if the issue persists',
    'Reach out to our support team for assistance',
  ];

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Stack spacing={3} alignItems="center" textAlign="center">
            {/* Error Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Error sx={{ fontSize: 48, color: 'error.main' }} />
            </Box>

            {/* Title */}
            <Typography variant="h4" fontWeight="bold" color="error.main">
              Payment Failed
            </Typography>

            {/* Message */}
            <Typography variant="body1" color="text.secondary">
              We were unable to process your payment. This could be due to
              insufficient funds, incorrect card details, or your payment was
              cancelled.
            </Typography>

            {/* Alert */}
            <Alert severity="warning" sx={{ width: '100%', textAlign: 'left' }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                No charges were made to your account
              </Typography>
              <Typography variant="body2">
                Your subscription remains unchanged. You can try again when ready.
              </Typography>
            </Alert>

            {/* Troubleshooting */}
            <Box sx={{ width: '100%', textAlign: 'left' }}>
              <Typography
                variant="subtitle2"
                fontWeight="medium"
                gutterBottom
                color="text.secondary"
              >
                Troubleshooting Steps:
              </Typography>
              <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
                {troubleshootingSteps.map((step, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={`${index + 1}. ${step}`}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Action Buttons */}
            <Stack spacing={2} width="100%" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Refresh />}
                onClick={() => navigate('/subscription')}
                fullWidth
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Home />}
                onClick={() => navigate('/dashboard')}
                fullWidth
              >
                Return to Dashboard
              </Button>
            </Stack>

            {/* Footer */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
              Need help? Contact our support team at support@pinepocket.com
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentFailed;

